import type { APIBookingDraft, APIBookingDraftParticipant } from '@/scopes/checkout/infrastructure/api/types'
import { type Country, countryData } from '@sunwebgroup/uds-ui/fields/phone-field'
import { PhoneNumber } from '@/scopes/checkout/domain/types'
import {
  CheckoutParticipantsDetails,
  Field,
  FieldOption,
  ParticipantFormFieldId,
  PostalCodeCountry,
} from '@/scopes/checkout/domain/types/checkoutPersonalDetails'
import { ParticipantFormFieldIds } from '@/scopes/checkout/domain/types/participantFormFieldIds'
import { ParticipantFormType } from '@/scopes/checkout/domain/types/participantFormType'
import { mapPhoneNumber } from '@/scopes/checkout/infrastructure/api/mapPhoneNumber'
import {
  Field as ApiField,
  BookingDraftParticipantsDetails,
  Fieldset,
  ParticipantForm,
} from '@/scopes/checkout/infrastructure/api/types/bookingDraftParticipantsDetails'

export interface CheckoutParticipantsDetailsMapper {
  map: (participantsDetails: BookingDraftParticipantsDetails, bookingDraft: APIBookingDraft) => CheckoutParticipantsDetails
}

type FieldValue = string | PhoneNumber | undefined | null | number

const requiredFields: ParticipantFormFieldId[] = [ParticipantFormFieldIds.Name, ParticipantFormFieldIds.Surname, ParticipantFormFieldIds.Title, ParticipantFormFieldIds.Birthdate]

export function createCheckoutParticipantsDetailsMapper(): CheckoutParticipantsDetailsMapper {
  const map = (participantsDetails: BookingDraftParticipantsDetails, bookingDraft: APIBookingDraft) => {
    const participantForms = participantsDetails.bookingParticipantsForm

    const fields = mapFields(bookingDraft, participantForms)
    const phoneCountries = mapPhoneCountries(participantForms)
    const postalCodeCountries = mapPostalCodeCountries(participantForms)
    return {
      fields,
      phoneCountries,
      postalCodeCountries,
    }
  }

  return {
    map,
  }
}

function mapFields(bookingDraft: APIBookingDraft, participantForms: ParticipantForm[]) {
  return bookingDraft.rooms.map((room) => {
    return {
      id: room.id.toString(),
      name: room.name,
      participants: room.participantIds.map((participantId) => {
        const participantForm = participantForms.find(form => form.participantIds.includes(participantId))

        if (!participantForm) {
          return {
            id: participantId,
            type: ParticipantFormType.Adult,
            fields: [],
          }
        }

        const fields = mapFieldsFromFieldsets(participantForm.fieldsets, participantId.toString())
        const participant = bookingDraft.participants.find(p => p.id === participantId)!
        const fieldsWithDefaultData = fields.map(field => populateFieldDefaultValue(field, participant))

        return {
          id: participantId,
          type: ParticipantFormType[participantForm.type] ?? ParticipantFormType.Adult,
          fields: fieldsWithDefaultData,
        }
      }),
    }
  })
}

function mapPhoneCountries(participantForms: ParticipantForm[]): Country[] {
  let isoPhoneCountriesAvailable = {} as Record<string, { iso2: string, name: string }>

  for (const form of participantForms) {
    for (const fieldset of form.fieldsets) {
      const mobilePhoneField = fieldset.fields.find(field => field.type === 'MobilePhoneNumber')
      if (mobilePhoneField) {
        const fieldAdditionalSettings = typeof mobilePhoneField.additionalSettings === 'string'
          ? JSON.parse(mobilePhoneField.additionalSettings)
          : mobilePhoneField.additionalSettings || {}

        isoPhoneCountriesAvailable = fieldAdditionalSettings.allCountries?.reduce((acc: Record<string, { iso2: string, name: string }>, country: { iso2: string, name: string }) => {
          acc[country.iso2.toLowerCase()] = { iso2: country.iso2.toLowerCase(), name: country.name }
          return acc
        }, {}) || {}
        break
      }
    }
  }

  return Object.keys(isoPhoneCountriesAvailable)
    .map((isoCode) => {
      const country = countryData.find(c => c.isoCode.toLowerCase() === isoCode)
      if (country) {
        return {
          ...country,
          name: isoPhoneCountriesAvailable[isoCode].name,
        }
      }
      return undefined
    })
    .filter((country): country is Country => country !== undefined)
}

function mapPostalCodeCountries(participantForms: ParticipantForm[]): PostalCodeCountry[] {
  let postalCodeCountries = []
  for (const form of participantForms) {
    for (const fieldset of form.fieldsets) {
      const postalCodeField = fieldset.fields.find(field => field.type === 'PostalCode')
      if (postalCodeField) {
        const fieldAdditionalSettings = typeof postalCodeField.additionalSettings === 'string'
          ? JSON.parse(postalCodeField.additionalSettings)
          : postalCodeField.additionalSettings || {}
        postalCodeCountries = fieldAdditionalSettings.allCountries?.map(({ iso2, pattern }: { iso2: string, pattern: string }) => {
          return { isoCode: iso2.toLowerCase(), pattern }
        }) || []
      }
    }
  }
  return postalCodeCountries
}

function mapFieldsFromFieldsets(fieldsets: Fieldset[], participantId: string): Field[] {
  return fieldsets
    .reduce<Field[]>(
      (mappedFields, fieldset) => [...mappedFields, ...checkoutFieldsMapper(fieldset.fields, participantId)],
      [],
    )
}

function checkoutFieldsMapper(fields: ApiField[], participantId: string): Field[] {
  return fields.map(field => ({
    id: field.type,
    isMandatory: mapMandatoryPropertyField(field),
    name: `${participantId}.${field.type}`,
    ...(field.pattern ? { validationPattern: mapFieldPattern(field) } : {}),
    ...(field.values ? { options: mapFieldValuesToOptions(field.values) } : {}),
    ...(field.type === ParticipantFormFieldIds.Birthdate && field.dateRange
      ? {
          dateRange: mapDateRange(field.dateRange),
        }
      : {}),
  }
  ))
}

function mapDateRange(dateRange: { startDate?: string, endDate?: string }): { min?: string, max?: string } {
  return {
    min: dateRange.startDate,
    max: dateRange.endDate,
  }
}
function mapFieldPattern(field: ApiField): string | undefined {
  // The pattern that comes from the API, contains a lot of double backslashes which makes it not work when used in the frontend,
  // so we are replacing it with a hardcoded pattern that works, and that should be equivalent to the one coming from the API.
  // Todo: We should fix this in the backend to avoid having to do this replacement in the frontend.
  if (ParticipantFormFieldIds.Name === field.type || ParticipantFormFieldIds.Surname === field.type || ParticipantFormFieldIds.CityName === field.type) {
    // return '^ *(?:[A-Za-zÆæØøÅå\'ÄäÖöÜüßÉéÀàÈèÙùÂâÊêÎîÔôÛûËëÏïŸÿÇçÁáÍíÓóÚú\-]{2,} *)+$'
    return '^[A-Za-zÆæØøÅåÄäÖöÜüßÉéÀàÈèÙùÂâÊêÎîÔôÛûËëÏïŸÿÇçÁáÍíÓóÚú\'\\-]{2,}( [A-Za-zÆæØøÅåÄäÖöÜüßÉéÀàÈèÙùÂâÊêÎîÔôÛûËëÏïŸÿÇçÁáÍíÓóÚú\'\\-]{2,})*$'
  }

  return field.pattern
}

function mapFieldValuesToOptions(values: { name: string, value: string }[]): FieldOption[] {
  const seen = new Set<string>()

  return values.reduce<FieldOption[]>((acc, { name, value }) => {
    if (!seen.has(value)) {
      seen.add(value)
      acc.push({ name, value })
    }
    return acc
  }, [])
}

function populateFieldDefaultValue(
  field: Field,
  participant: APIBookingDraftParticipant,
) {
  const participantFieldKey = pascalCaseToCamelCase(field.id) as keyof typeof participant
  const defaultValue: FieldValue = participant[participantFieldKey]

  if (!defaultValue) {
    return field
  }

  const phoneNumberDefaultValue = mapPhoneNumber(defaultValue.toString())
  if (phoneNumberDefaultValue) {
    return {
      ...field,
      defaultValue: phoneNumberDefaultValue,
    }
  }

  if (field.id === 'Title') {
    if (defaultValue === 'NotSet') {
      return field
    }
  }

  if (field.id === 'Birthdate') {
    return {
      ...field,
      defaultValue: new Date(defaultValue),
    }
  }

  return {
    ...field,
    defaultValue: defaultValue.toString(),
  }
}

function pascalCaseToCamelCase(string: string) {
  return string.charAt(0).toLowerCase() + string.slice(1)
}

function mapMandatoryPropertyField(field: ApiField): boolean {
  if (requiredFields.includes(field.type as ParticipantFormFieldId)) {
    return true
  }

  return field.isMandatory
}
